import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Target } from 'lucide-react';
import { fetchJSON } from '../lib/api-client';

/** 對應後端 lib/trade-plans.ts TradePlanSummaryRow */
interface SummaryRow {
  key: string;
  total: number;
  entered: number;
  closed: number;
  win: number;
  loss: number;
  winRate: number;
  avgR: number;
  profitFactor: number | null;
  maxDD: number;
  expectancy: number;
}
interface MoneyBucket {
  currency: 'TWD' | 'USD';
  capitalPerPick: number;
  closed: number;
  realizedPnl: number;
  wins: number;
  losses: number;
}
interface SummaryResp {
  days: number;
  groupBy: string;
  rows: SummaryRow[];
  money: { twd: MoneyBucket; usd: MoneyBucket };
}

/** 對應後端 lib/trade-plans.ts OpenPosition / OpenPositionsResult */
interface OpenPosition {
  id: string;
  symbol: string;
  market: 'tw' | 'us';
  currency: 'TWD' | 'USD';
  status: string;
  entryPrice: number;
  fillPrice: number;
  stopLoss: number;
  currentStop?: number;
  t1: number;
  t2: number;
  plannedQuantity: number;
  remainingQuantity: number;
  confidence?: number;
  createdAt: string;
  enteredAt?: string;
  validUntil: string;
  currentPrice?: number;
  unrealizedPnl?: number;
  unrealizedPnlPct?: number;
  realizedPnl?: number;
  quoteSource: 'yahoo' | 'unavailable';
}
interface OpenTotalsBucket {
  currency: 'TWD' | 'USD';
  count: number;
  cost: number;
  marketValue: number;
  unrealizedPnl: number;
  realizedPnl: number;
}
interface OpenResp {
  asOf: string;
  positions: OpenPosition[];
  totals: { twd: OpenTotalsBucket; usd: OpenTotalsBucket };
}

type Tab = 'live' | 'stats';
type GroupBy = 'confidenceBucket' | 'symbol' | 'lens' | 'model';

const GROUP_OPTIONS: { id: GroupBy; label: string }[] = [
  { id: 'confidenceBucket', label: '依信心' },
  { id: 'symbol', label: '依標的' },
  { id: 'lens', label: '依快篩' },
  { id: 'model', label: '依模型' },
];
const DAYS_OPTIONS = [7, 30, 90];

/** 結案樣本量低於此值時，勝率/平均R 統計上不可靠，需提示。 */
const MIN_RELIABLE_SAMPLE = 10;

const STATUS_LABEL: Record<string, string> = {
  pending: '待進場',
  entered: '持有中',
  partial_t1: '已過 T1',
  partial_t2: '已過 T2',
  still_holding: '逾期續抱',
};

function pct(v: number, digits = 0): string {
  return `${(v * 100).toFixed(digits)}%`;
}
function signedPct(v: number | undefined, digits = 1): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return '—';
  return `${v >= 0 ? '+' : ''}${(v * 100).toFixed(digits)}%`;
}
function money(v: number | undefined, currency: 'TWD' | 'USD'): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return '—';
  const digits = currency === 'TWD' ? 0 : 2;
  const sym = currency === 'TWD' ? 'NT$' : '$';
  return `${v < 0 ? '-' : ''}${sym}${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: digits })}`;
}
function priceText(v: number | undefined): string {
  return typeof v === 'number' && Number.isFinite(v) ? v.toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—';
}
function pnlTone(v: number | undefined): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return 'text-(--text-secondary)';
  return v > 0 ? 'text-(--up)' : v < 0 ? 'text-(--down)' : 'text-(--text-secondary)';
}
function rTone(v: number): string {
  return v > 0.05 ? 'text-(--up)' : v < -0.05 ? 'text-(--down)' : 'text-(--text-secondary)';
}
function keyLabel(groupBy: string, key: string): string {
  if (key === 'all') return '全部';
  if (key === 'none') return '（無快篩）';
  if (key === 'unknown') return '（未知）';
  if (groupBy === 'confidenceBucket') return `${key}%`;
  return key;
}
function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

export function TradePlanHitRatePanel({ onSelectSymbol, embedded = false }: { onSelectSymbol?: (symbol: string) => void; embedded?: boolean } = {}) {
  const [tab, setTab] = useState<Tab>('live');
  const [groupBy, setGroupBy] = useState<GroupBy>('confidenceBucket');
  const [days, setDays] = useState(30);
  const [showDetail, setShowDetail] = useState(false);

  const summaryQ = useQuery({
    queryKey: ['trade-plan-summary', days, groupBy],
    queryFn: () => fetchJSON<SummaryResp>(`/api/trade-plans/summary?days=${days}&groupBy=${groupBy}`),
    staleTime: 5 * 60_000,
    refetchInterval: 10 * 60_000,
    retry: 1,
  });
  const openQ = useQuery({
    queryKey: ['trade-plans-open'],
    queryFn: () => fetchJSON<OpenResp>('/api/trade-plans/open?days=60'),
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
    retry: 1,
  });

  const rows = summaryQ.data?.rows ?? [];
  const moneySummary = summaryQ.data?.money;
  const overall = useMemo(() => {
    const total = rows.reduce((s, r) => s + r.total, 0);
    const entered = rows.reduce((s, r) => s + r.entered, 0);
    const closed = rows.reduce((s, r) => s + r.closed, 0);
    const win = rows.reduce((s, r) => s + r.win, 0);
    const rWeighted = rows.reduce((s, r) => s + r.avgR * r.closed, 0);
    return {
      total,
      entered,
      closed,
      winRate: closed ? win / closed : 0,
      avgR: closed ? rWeighted / closed : 0,
    };
  }, [rows]);

  const positions = openQ.data?.positions ?? [];
  const totals = openQ.data?.totals;

  return (
    <div className={embedded ? '' : 'card mt-2'}>
      <div className="mb-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          {!embedded && <Target size={14} className="text-(--accent)" />}
          {!embedded && <span className="text-sm font-bold text-(--text-primary)">模擬跟單組合</span>}
          <span className="text-[11px] text-(--text-muted) hidden sm:inline">每檔以滿額購買力試算 · 真實回放、不是「方向猜對」</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setTab('live')}
            className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${tab === 'live' ? 'border-(--accent) bg-(--accent)/15 text-(--accent)' : 'border-(--border) text-(--text-secondary) hover:border-(--accent)'}`}
          >
            即時持倉
          </button>
          <button
            type="button"
            onClick={() => setTab('stats')}
            className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${tab === 'stats' ? 'border-(--accent) bg-(--accent)/15 text-(--accent)' : 'border-(--border) text-(--text-secondary) hover:border-(--accent)'}`}
          >
            命中率統計
          </button>
        </div>
      </div>

      {tab === 'live' ? (
        <LiveTab
          positions={positions}
          totals={totals}
          isLoading={openQ.isLoading}
          isError={openQ.isError}
          error={openQ.error as Error | null}
          onSelectSymbol={onSelectSymbol}
        />
      ) : (
        <StatsTab
          rows={rows}
          money={moneySummary}
          overall={overall}
          days={days}
          setDays={setDays}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          showDetail={showDetail}
          setShowDetail={setShowDetail}
          isLoading={summaryQ.isLoading}
          isError={summaryQ.isError}
          error={summaryQ.error as Error | null}
        />
      )}
    </div>
  );
}

function LiveTab({
  positions,
  totals,
  isLoading,
  isError,
  error,
  onSelectSymbol,
}: {
  positions: OpenPosition[];
  totals?: { twd: OpenTotalsBucket; usd: OpenTotalsBucket };
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onSelectSymbol?: (symbol: string) => void;
}) {
  const [showPositions, setShowPositions] = useState(false);
  return (
    <>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <MoneyCard label="台股未實現損益" bucket={totals?.twd} isLoading={isLoading} />
        <MoneyCard label="美股未實現損益" bucket={totals?.usd} isLoading={isLoading} />
      </div>

      {isError && (
        <div className="rounded border border-(--warn)/40 bg-(--warn)/10 px-3 py-2 text-[12px] text-(--warn)">
          即時持倉載入失敗：{error?.message}
        </div>
      )}

      {!isError && !isLoading && positions.length === 0 && (
        <div className="rounded border border-(--border) bg-(--bg-base) px-3 py-4 text-center text-[12px] text-(--text-muted)">
          目前沒有進場中或待進場的模擬持倉。每日盤後企鵝會自動把今日 buy/add 進場機會建成計畫，當天先以「待進場」列出，觸發進場後轉持有並逐日更新損益。
        </div>
      )}

      {positions.length > 0 && (
        <button
          type="button"
          onClick={() => setShowPositions((v) => !v)}
          className="mb-2 flex w-full items-center justify-between gap-2 rounded border border-(--border) bg-(--bg-base) px-2.5 py-1.5 text-left hover:bg-(--bg-soft)"
        >
          <span className="text-[12px] font-semibold text-(--text-primary)">持倉明細（{positions.length} 檔標的）</span>
          <span className="text-[10px] text-(--text-muted)">{showPositions ? '收起 ▲' : '展開 ▼'}</span>
        </button>
      )}

      {positions.length > 0 && showPositions && (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-(--text-muted) border-b border-(--border)">
                <th className="px-2 py-1 text-left font-semibold">標的</th>
                <th className="px-2 py-1 text-left font-semibold">狀態</th>
                <th className="px-2 py-1 text-right font-semibold">進場</th>
                <th className="px-2 py-1 text-right font-semibold">現價</th>
                <th className="px-2 py-1 text-right font-semibold">股數</th>
                <th className="px-2 py-1 text-right font-semibold">未實現損益</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p) => (
                <tr
                  key={p.id}
                  className={`border-b border-(--border)/50 ${onSelectSymbol ? 'cursor-pointer hover:bg-(--bg-hover)' : ''}`}
                  onClick={onSelectSymbol ? () => onSelectSymbol(p.symbol) : undefined}
                >
                  <td className="px-2 py-1 text-left font-semibold text-(--text-primary)">{p.symbol}</td>
                  <td className="px-2 py-1 text-left text-(--text-secondary)">{statusLabel(p.status)}</td>
                  <td className="px-2 py-1 text-right text-(--text-secondary)">{priceText(p.fillPrice)}</td>
                  <td className="px-2 py-1 text-right text-(--text-secondary)">
                    {p.quoteSource === 'unavailable' ? '報價中斷' : priceText(p.currentPrice)}
                  </td>
                  <td className="px-2 py-1 text-right text-(--text-secondary)">{p.remainingQuantity.toLocaleString()}</td>
                  <td className={`px-2 py-1 text-right font-bold ${pnlTone(p.unrealizedPnl)}`}>
                    {p.status === 'pending' ? (
                      <span className="text-[10px] font-normal text-(--text-muted)">待進場</span>
                    ) : (
                      <>
                        {money(p.unrealizedPnl, p.currency)}
                        <span className="ml-1 text-[10px] font-normal">{signedPct(p.unrealizedPnlPct)}</span>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-2 text-[10px] text-(--text-muted) leading-relaxed">
        只追蹤「進場機會」訊號（今日行動 + 盤中機會），同一檔只顯示最新一筆；隨手查個股不列入。「待進場」是今天剛跟到、尚未經回放確認成交的計畫，盤中先列出讓你知道跟了哪幾支，不計入上方未實現損益卡與命中率（等收盤回放確認進場才算）。每檔以「滿額購買力」獨立試算（台幣 50 萬 / 美金 1.5 萬都用滿），方便比較每檔賺賠；非單一帳戶分倉。AI 輔助、扳機自負。
      </div>
    </>
  );
}

function MoneyCard({ label, bucket, isLoading }: { label: string; bucket?: OpenTotalsBucket; isLoading: boolean }) {
  const currency = bucket?.currency ?? (label.includes('台') ? 'TWD' : 'USD');
  return (
    <div className="rounded border border-(--border) bg-(--bg-base) px-3 py-2">
      <div className="text-[10px] text-(--text-muted)">{label}</div>
      <div className={`text-base font-black ${pnlTone(bucket?.unrealizedPnl)}`}>
        {isLoading ? '…' : money(bucket?.unrealizedPnl, currency)}
      </div>
      <div className="text-[10px] text-(--text-muted) mt-0.5">
        {isLoading ? '' : `${bucket?.count ?? 0} 檔持倉 · 市值 ${money(bucket?.marketValue, currency)}`}
      </div>
    </div>
  );
}

function StatsTab({
  rows,
  money: moneySummary,
  overall,
  days,
  setDays,
  groupBy,
  setGroupBy,
  showDetail,
  setShowDetail,
  isLoading,
  isError,
  error,
}: {
  rows: SummaryRow[];
  money?: { twd: MoneyBucket; usd: MoneyBucket };
  overall: { total: number; entered: number; closed: number; winRate: number; avgR: number };
  days: number;
  setDays: (d: number) => void;
  groupBy: GroupBy;
  setGroupBy: (g: GroupBy) => void;
  showDetail: boolean;
  setShowDetail: (fn: (v: boolean) => boolean) => void;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}) {
  return (
    <>
      <div className="mb-2 flex items-center justify-end gap-1">
        {DAYS_OPTIONS.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            className={`rounded border px-2 py-0.5 text-[10px] font-semibold ${
              days === d ? 'border-(--accent) bg-(--accent)/15 text-(--accent)' : 'border-(--border) text-(--text-secondary) hover:border-(--accent)'
            }`}
          >
            {d}天
          </button>
        ))}
      </div>

      {/* Hero 數字 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-2">
        <Stat label="計畫總數" value={isLoading ? '…' : String(overall.total)} />
        <Stat label="已進場" value={isLoading ? '…' : String(overall.entered)} />
        <Stat label="已結案" value={isLoading ? '…' : String(overall.closed)} />
        <Stat label="勝率" value={isLoading ? '…' : overall.closed ? pct(overall.winRate) : '—'} highlight />
        <Stat label="平均 R" value={isLoading ? '…' : overall.closed ? overall.avgR.toFixed(2) : '—'} highlight tone={rTone(overall.avgR)} />
      </div>

      {/* 已實現損益（金額） */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <MoneyStat label="台股已實現損益" bucket={moneySummary?.twd} isLoading={isLoading} />
        <MoneyStat label="美股已實現損益" bucket={moneySummary?.usd} isLoading={isLoading} />
      </div>

      {isError && (
        <div className="rounded border border-(--warn)/40 bg-(--warn)/10 px-3 py-2 text-[12px] text-(--warn)">
          命中率載入失敗：{error?.message}
        </div>
      )}

      {!isError && overall.closed === 0 && !isLoading && (
        <div className="rounded border border-(--border) bg-(--bg-base) px-3 py-4 text-center text-[12px] text-(--text-muted)">
          這段期間還沒有結案的模擬計畫。每日盤後自動建單 + 回放結案後，這裡就會累積真實勝率與賺賠金額。
        </div>
      )}

      {!isError && !isLoading && overall.closed > 0 && overall.closed < MIN_RELIABLE_SAMPLE && (
        <div className="mb-2 rounded border border-(--warn)/40 bg-(--warn)/10 px-3 py-2 text-[12px] text-(--warn)">
          樣本量偏低（僅 {overall.closed} 筆結案，需 ≥ {MIN_RELIABLE_SAMPLE} 筆才有統計參考性）。目前勝率/平均R 容易被個別交易左右，先當參考、別當定論。
        </div>
      )}

      {/* 展開詳細分組 */}
      {!isError && rows.some((r) => r.total > 0) && (
        <button
          type="button"
          onClick={() => setShowDetail((v) => !v)}
          className="inline-flex items-center gap-1 rounded border border-(--border) px-2 py-0.5 text-[11px] font-semibold text-(--text-secondary) hover:border-(--accent) hover:text-(--accent)"
        >
          {showDetail ? '✕ 收起分組明細' : '▸ 展開分組明細（依信心 / 標的 / 快篩 / 模型）'}
        </button>
      )}

      {showDetail && !isError && rows.some((r) => r.total > 0) && (
        <>
          <div className="mt-2 mb-2 flex flex-wrap gap-1">
            {GROUP_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setGroupBy(opt.id)}
                className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${
                  groupBy === opt.id ? 'border-(--accent) bg-(--accent)/15 text-(--accent)' : 'border-(--border) text-(--text-secondary) hover:border-(--accent)'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-(--text-muted) border-b border-(--border)">
                  <th className="px-2 py-1 text-left font-semibold">{GROUP_OPTIONS.find((g) => g.id === groupBy)?.label}</th>
                  <th className="px-2 py-1 text-right font-semibold">總數</th>
                  <th className="px-2 py-1 text-right font-semibold">結案</th>
                  <th className="px-2 py-1 text-right font-semibold">勝/負</th>
                  <th className="px-2 py-1 text-right font-semibold">勝率</th>
                  <th className="px-2 py-1 text-right font-semibold">平均R</th>
                  <th className="px-2 py-1 text-right font-semibold">獲利因子</th>
                  <th className="px-2 py-1 text-right font-semibold">最大回落</th>
                </tr>
              </thead>
              <tbody>
                {rows.filter((r) => r.total > 0).map((r) => {
                  const lowSample = r.closed > 0 && r.closed < 5;
                  return (
                    <tr key={r.key} className="border-b border-(--border)/50">
                      <td className="px-2 py-1 text-left font-semibold text-(--text-primary)">
                        {keyLabel(groupBy, r.key)}
                        {lowSample && <span className="ml-1 text-[9px] font-normal text-(--text-muted)" title="結案樣本少於 5 筆，數據參考性低">·小樣本</span>}
                      </td>
                      <td className="px-2 py-1 text-right text-(--text-secondary)">{r.total}</td>
                      <td className="px-2 py-1 text-right text-(--text-secondary)">{r.closed}</td>
                      <td className="px-2 py-1 text-right text-(--text-secondary)">{r.win}/{r.loss}</td>
                      <td className={`px-2 py-1 text-right font-bold ${lowSample ? 'text-(--text-muted)' : 'text-(--text-primary)'}`}>{r.closed ? pct(r.winRate) : '—'}</td>
                      <td className={`px-2 py-1 text-right font-bold ${lowSample ? 'text-(--text-muted)' : rTone(r.avgR)}`}>{r.closed ? r.avgR.toFixed(2) : '—'}</td>
                      <td className="px-2 py-1 text-right text-(--text-secondary)">{r.profitFactor == null ? '∞' : r.profitFactor.toFixed(2)}</td>
                      <td className="px-2 py-1 text-right text-(--down)">{r.maxDD ? pct(r.maxDD) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

function MoneyStat({ label, bucket, isLoading }: { label: string; bucket?: MoneyBucket; isLoading: boolean }) {
  const currency = bucket?.currency ?? (label.includes('台') ? 'TWD' : 'USD');
  return (
    <div className="rounded border border-(--border) bg-(--bg-base) px-3 py-2">
      <div className="text-[10px] text-(--text-muted)">{label}</div>
      <div className={`text-base font-black ${pnlTone(bucket?.realizedPnl)}`}>
        {isLoading ? '…' : money(bucket?.realizedPnl, currency)}
      </div>
      <div className="text-[10px] text-(--text-muted) mt-0.5">
        {isLoading ? '' : `${bucket?.closed ?? 0} 筆結案 · 勝 ${bucket?.wins ?? 0} / 負 ${bucket?.losses ?? 0}`}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight, tone }: { label: string; value: string; highlight?: boolean; tone?: string }) {
  return (
    <div className="rounded border border-(--border) bg-(--bg-base) px-2 py-1.5 text-center">
      <div className="text-[10px] text-(--text-muted)">{label}</div>
      <div className={`${highlight ? 'text-base font-black' : 'text-sm font-bold'} ${tone ?? 'text-(--text-primary)'}`}>{value}</div>
    </div>
  );
}
